/* eslint-disable no-console, @typescript-eslint/consistent-type-imports */
/**
 * Monitoring & Error Tracking
 *
 * Integrates with Sentry when VITE_SENTRY_DSN is set.
 * Falls back to structured console output when Sentry is not configured.
 *
 * Setup:
 *   1. npm install @sentry/react
 *   2. Set VITE_SENTRY_DSN in your .env (see .env.example)
 *   3. Call initMonitoring() in main.tsx before rendering
 */

export interface MonitoringUser {
  id: string;
  email?: string;
  name?: string;
}

// Dynamically-loaded Sentry instance — only populated when DSN is configured
let sentry: typeof import('@sentry/react') | null = null;

/**
 * Initialise monitoring. Call once at application startup (main.tsx).
 * No-ops silently if VITE_SENTRY_DSN is not set.
 */
export async function initMonitoring(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION as string | undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Low sample rates — tune per your traffic volume
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event, hint) {
        const err = hint?.originalException;
        // Drop transient network errors — these are client-side (VPN, offline, etc.)
        // and are not actionable bugs in our code.
        if (err instanceof TypeError) {
          const msg = err.message?.toLowerCase() ?? '';
          if (
            msg === 'failed to fetch' ||
            msg === 'networkerror when attempting to fetch resource.' ||
            msg.includes('load failed') ||
            msg.includes('network request failed')
          ) {
            return null;
          }
        }
        if (err instanceof DOMException && err.name === 'AbortError') {
          return null;
        }
        return event;
      },
    });
    sentry = Sentry;
  } catch {
    // @sentry/react not installed — monitoring disabled
  }
}

/**
 * Capture an exception and send to Sentry (or log to console in dev).
 */
export function captureException(
  error: Error | unknown,
  context?: Record<string, unknown>
): void {
  if (sentry) {
    sentry.captureException(error, { extra: context });
  } else if (import.meta.env.DEV) {
    console.error('[Monitoring] Exception captured:', error, context);
  }
}

/**
 * Capture a message event.
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  if (sentry) {
    sentry.captureMessage(message, level);
  } else if (import.meta.env.DEV) {
    console.log(`[Monitoring] ${level.toUpperCase()}: ${message}`);
  }
}

/**
 * Identify the current user for error attribution.
 * Call after successful authentication.
 */
export function identifyUser(user: MonitoringUser | null): void {
  if (!sentry) return;
  sentry.setUser(user ? { id: user.id, email: user.email, username: user.name } : null);
}

/**
 * Add breadcrumb for user action tracking.
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  if (!sentry) return;
  sentry.addBreadcrumb({ message, category, data, level: 'info' });
}
